/**
 * InstallAppPrompt (iter122) — Add-to-Home-Screen banner surfaced on the
 * Simplified launchers (student + coach).
 *
 * Behaviour:
 *   · Chrome/Edge/Android: shows an "Install app" pill that fires the native
 *     install prompt when tapped.
 *   · iOS Safari: shows a "Add to Home Screen" pill that opens an
 *     instructions sheet (Safari doesn't expose beforeinstallprompt).
 *   · Already-installed (display-mode: standalone) or user-dismissed: renders
 *     nothing so we don't nag.
 *
 * Dismissal is soft (30-day snooze via localStorage) so users who tap X can
 * still find the button elsewhere later.
 */
import { useEffect, useState } from "react";
import { Download, Share2, Plus, X, Bell } from "lucide-react";
import {
  subscribeInstallPrompt,
  triggerInstall,
  isStandaloneDisplay,
  isIOSSafari,
  ensurePushSubscription,
  pushSupported,
  pushPermission,
} from "@/lib/pwa";

const DISMISS_KEY = "rl_install_prompt_dismissed_at";
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isSnoozed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!at) return false;
    return Date.now() - at < SNOOZE_MS;
  } catch { return false; }
}

export default function InstallAppPrompt() {
  const [canPrompt, setCanPrompt] = useState(false);
  const [showIOSSheet, setShowIOSSheet] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isStandaloneDisplay()) setInstalled(true);
    setDismissed(isSnoozed());
    setInitialized(true);
    const unsub = subscribeInstallPrompt((evt) => {
      setCanPrompt(!!evt);
    });
    // Iter125 — After a successful install, opportunistically also register
    // the push subscription so cascade + approval pings work.
    if (isStandaloneDisplay() && pushSupported() && pushPermission() === "granted") {
      ensurePushSubscription().catch(() => {});
    }
    return () => unsub && unsub();
  }, []);

  // Iter125 — Standalone push-only nudge. Shown when the app is already
  // installed and the user hasn't granted notifications yet (a common case
  // — install ≠ subscribe).
  const showPushOnly =
    installed && pushSupported() && pushPermission() !== "granted" && pushPermission() !== "denied";

  if (!initialized || dismissed) return null;
  if (installed && !showPushOnly) return null;

  const ios = isIOSSafari();
  // Nothing useful to offer if:
  //  · Not iOS Safari AND we never captured beforeinstallprompt (probably
  //    already installable via browser menu; keep silent).
  if (!ios && !canPrompt) return null;

  const handleInstall = async () => {
    if (showPushOnly) {
      const { status: st } = await ensurePushSubscription();
      if (st === "granted") setInstalled(true);
      return;
    }
    if (ios) {
      setShowIOSSheet(true);
      return;
    }
    const { outcome } = await triggerInstall();
    if (outcome === "accepted") {
      setInstalled(true);
      // Iter125 — Auto-subscribe once the install is accepted so the user
      // doesn't have to hunt for another button.
      ensurePushSubscription().catch(() => {});
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* silent */ }
    setDismissed(true);
  };

  return (
    <>
      <div
        data-testid="install-app-prompt"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          marginTop: 16,
          padding: "10px 14px",
          border: "1px solid var(--border, #262626)",
          borderRadius: 999,
          background: "rgba(204, 255, 0, 0.08)",
          color: "var(--text-strong, #fff)",
          fontSize: 12,
          letterSpacing: "0.02em",
        }}
      >
        <button
          type="button"
          onClick={handleInstall}
          data-testid="install-app-cta"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "transparent",
            color: "inherit",
            border: "none",
            padding: 0,
            font: "inherit",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: 999,
              background: "#CCFF00",
              color: "#0A0A0A",
            }}
            aria-hidden
          >
            <Download style={{ width: 14, height: 14 }} strokeWidth={2.25} />
          </span>
          <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {showPushOnly ? "Enable Notifications" : ios ? "Add to Home Screen" : "Install App"}
          </span>
          <span style={{ color: "var(--muted, #A3A3A3)", textTransform: "none", letterSpacing: 0 }}>
            {showPushOnly ? "· get pinged the moment a coach approves" : "· one-tap access on-court"}
          </span>
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="install-app-dismiss"
          aria-label="Dismiss install prompt"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            border: "none",
            borderRadius: 999,
            background: "transparent",
            color: "var(--muted, #A3A3A3)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>

      {showIOSSheet && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="install-ios-sheet"
          onClick={() => setShowIOSSheet(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 480,
              background: "#0A0A0A",
              border: "1px solid #262626",
              borderRadius: 20,
              padding: "24px 22px",
              color: "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>Add Rally Lab to your Home Screen</div>
              <button
                type="button"
                onClick={() => setShowIOSSheet(false)}
                data-testid="install-ios-close"
                aria-label="Close"
                style={{ background: "transparent", color: "#A3A3A3", border: "none", cursor: "pointer" }}
              >
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
              <li style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 28, height: 28, borderRadius: 999, background: "#141414", color: "#CCFF00", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>1</span>
                <span style={{ fontSize: 14, color: "#E5E5E5", lineHeight: 1.5 }}>
                  Tap the <Share2 style={{ width: 14, height: 14, verticalAlign: "middle" }} /> <strong>Share</strong> button in the Safari toolbar.
                </span>
              </li>
              <li style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 28, height: 28, borderRadius: 999, background: "#141414", color: "#CCFF00", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>2</span>
                <span style={{ fontSize: 14, color: "#E5E5E5", lineHeight: 1.5 }}>
                  Scroll and choose <Plus style={{ width: 14, height: 14, verticalAlign: "middle" }} /> <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 28, height: 28, borderRadius: 999, background: "#141414", color: "#CCFF00", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>3</span>
                <span style={{ fontSize: 14, color: "#E5E5E5", lineHeight: 1.5 }}>
                  Tap <strong>Add</strong>. Rally Lab lives on your home screen — one tap, no browser bar.
                </span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
